import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { CANDIDATE, NAV_LINKS } from "@/data/campaign";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-paper/90 backdrop-blur-md border-b border-pine-700/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 sm:py-4">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-pine-700 font-serif text-lg font-semibold text-paper transition-colors group-hover:bg-terra-500">
              AN
            </span>
            <span className="leading-tight">
              <span className="block font-serif text-base font-semibold tracking-tight">{CANDIDATE.name}</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-pine-600">Ward 11 · Mississauga</span>
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop-menu">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-terra-500 ${
                      isActive ? "text-terra-500" : "text-ink/70"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <Link
              to="/donate"
              data-testid="nav-cta-donate"
              className="rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
            >
              Donate
            </Link>
            <Link
              to="/get-involved"
              data-testid="nav-cta-get-involved"
              className="group hidden items-center gap-1.5 border border-pine-700/30 px-5 py-2.5 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500 lg:flex"
            >
              Get Involved
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center border border-pine-700/20 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-pine-900 text-paper"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-serif text-lg font-semibold">{CANDIDATE.name}</span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-paper/20"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: "easeOut" }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block border-b border-paper/10 py-4 font-serif text-3xl font-medium tracking-tight transition-colors hover:text-terra-400"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + NAV_LINKS.length * 0.06, duration: 0.4, ease: "easeOut" }}
              >
                <NavLink
                  to="/donate"
                  data-testid="nav-mobile-link-donate"
                  className="block border-b border-paper/10 py-4 font-serif text-3xl font-medium tracking-tight text-blue-400 transition-colors hover:text-blue-300"
                >
                  Donate
                </NavLink>
              </motion.div>
            </nav>
            <div className="px-8 pb-10 font-mono text-xs uppercase tracking-[0.2em] text-paper/50">
              {CANDIDATE.phone} · {CANDIDATE.email}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
